import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { LogOut, Trash2, Check, X, Inbox, Star, MessageSquare, Mail, Phone, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ASSETS } from "@/lib/constants";
import { useSEO } from "@/hooks/useSEO";

const CATEGORY_LABELS = {
  training: "Training",
  contact: "Contact",
  tryout: "AAU Tryout",
  clinic: "Summer Clinic",
  "3v3": "Winter 3v3",
  join: "Join",
};

const CAT_COLORS = {
  training: "bg-blaze-blue/20 text-blaze-blueLight border-blaze-blue/40",
  contact: "bg-white/10 text-white/80 border-white/20",
  tryout: "bg-blaze-orange/20 text-blaze-orange border-blaze-orange/40",
  clinic: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "3v3": "bg-sky-500/15 text-sky-300 border-sky-500/30",
  join: "bg-purple-500/15 text-purple-300 border-purple-500/30",
};

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div className="text-sm">
      <span className="text-white/45">{label}: </span>
      <span className="text-white/85">{value}</span>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useSEO({ title: "Admin Dashboard | CT BLAZERS", description: "CT BLAZERS admin dashboard.", path: "/admin" });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { category: filter };
      const [s, subs, tst] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/submissions", { params }),
        api.get("/admin/testimonials"),
      ]);
      setStats(s.data);
      setSubmissions(subs.data);
      setTestimonials(tst.data);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const refreshStats = useCallback(async () => {
    try {
      const s = await api.get("/admin/stats");
      setStats(s.data);
    } catch {
      /* non-blocking */
    }
  }, []);

  const deleteSubmission = async (id) => {
    try {
      await api.delete(`/admin/submissions/${id}`);
      setSubmissions((s) => s.filter((x) => x.id !== id));
      refreshStats();
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const setApproval = async (id, approved) => {
    try {
      await api.patch(`/admin/testimonials/${id}`, { approved });
      setTestimonials((t) => t.map((x) => (x.id === id ? { ...x, approved } : x)));
      refreshStats();
      toast.success(approved ? "Approved & published" : "Unpublished");
    } catch {
      toast.error("Update failed");
    }
  };

  const deleteTestimonial = async (id) => {
    try {
      await api.delete(`/admin/testimonials/${id}`);
      setTestimonials((t) => t.filter((x) => x.id !== id));
      refreshStats();
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-ink-900 text-white">
      <header className="glass border-b border-white/10 sticky top-0 z-40">
        <div className="container-x flex items-center justify-between py-3">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={ASSETS.logo} alt="CT Blazers logo" className="h-10 w-auto" />
            <span className="font-heading text-xl tracking-wide">CT BLAZERS · Admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-white/50">{user?.email}</span>
            <Button
              variant="ghost"
              onClick={loadAll}
              data-testid="admin-refresh"
              className="text-white/70 hover:bg-white/10"
              size="icon"
            >
              <RefreshCw size={17} className={loading ? "animate-spin" : ""} />
            </Button>
            <Button
              onClick={logout}
              data-testid="admin-logout"
              className="bg-white/5 hover:bg-red-500/20 border border-white/10 text-white/80 rounded-full"
            >
              <LogOut size={16} className="mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="container-x py-8">
        <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl">Dashboard</h1>

        {stats && (
          <div className="mt-6 grid gap-4 grid-cols-2 lg:grid-cols-4">
            <StatCard icon={Inbox} label="Total Inquiries" value={stats.total_submissions} />
            <StatCard icon={MessageSquare} label="Testimonials" value={stats.testimonials_total} />
            <StatCard icon={Star} label="Pending Reviews" value={stats.testimonials_pending} accent />
            <StatCard icon={Inbox} label="Training Inquiries" value={stats.by_category?.training ?? 0} />
          </div>
        )}

        <Tabs defaultValue="submissions" className="mt-8">
          <TabsList className="bg-ink-700 border border-white/10">
            <TabsTrigger value="submissions" data-testid="tab-submissions" className="data-[state=active]:bg-blaze-blue data-[state=active]:text-white">
              Inquiries ({submissions.length})
            </TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="tab-testimonials" className="data-[state=active]:bg-blaze-blue data-[state=active]:text-white">
              Testimonials ({testimonials.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="submissions" className="mt-5">
            <div className="mb-4 flex items-center gap-3">
              <span className="text-sm text-white/50">Filter:</span>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger data-testid="filter-category" className="w-56 bg-ink-700 border-white/15 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-ink-800 border-white/15 text-white">
                  <SelectItem value="all">All Categories</SelectItem>
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {submissions.length === 0 ? (
              <EmptyState label="No inquiries yet." />
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {submissions.map((s) => (
                  <div key={s.id} data-testid={`submission-${s.id}`} className="rounded-2xl border border-white/10 bg-ink-700 p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <Badge className={`border ${CAT_COLORS[s.category] || "bg-white/10"} font-bold uppercase tracking-wider text-[10px]`}>
                          {CATEGORY_LABELS[s.category] || s.category}
                        </Badge>
                        <h3 className="mt-2 font-heading text-2xl tracking-wide leading-none">{s.name}</h3>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        data-testid={`delete-submission-${s.id}`}
                        onClick={() => deleteSubmission(s.id)}
                        className="text-white/40 hover:text-red-400 hover:bg-red-500/10 shrink-0"
                      >
                        <Trash2 size={17} />
                      </Button>
                    </div>
                    <div className="mt-3 space-y-1.5">
                      <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
                        <a href={`mailto:${s.email}`} className="flex items-center gap-1.5 text-blaze-blueLight hover:text-blaze-orange">
                          <Mail size={14} /> {s.email}
                        </a>
                        {s.phone && (
                          <span className="flex items-center gap-1.5 text-white/70">
                            <Phone size={14} /> {s.phone}
                          </span>
                        )}
                      </div>
                      <Field label="Parent/Guardian" value={s.parent_name} />
                      <Field label="Coach" value={s.coach} />
                      <Field label="Training Type" value={s.training_type} />
                      <Field label="Program" value={s.program} />
                      <Field label="Age/Grade" value={s.player_age} />
                      <Field label="Preferred Times" value={s.preferred_times} />
                      <Field label="Subject" value={s.subject} />
                      {s.message && (
                        <p className="mt-2 rounded-lg bg-ink-900/60 p-3 text-sm text-white/75 leading-relaxed">{s.message}</p>
                      )}
                    </div>
                    <p className="mt-3 text-xs text-white/35">{fmtDate(s.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="testimonials" className="mt-5">
            {testimonials.length === 0 ? (
              <EmptyState label="No testimonials submitted yet." />
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {testimonials.map((t) => (
                  <div key={t.id} data-testid={`admin-testimonial-${t.id}`} className="rounded-2xl border border-white/10 bg-ink-700 p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <h3 className="font-heading text-2xl tracking-wide leading-none">{t.name}</h3>
                        <Badge className={t.approved ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" : "bg-yellow-500/15 text-yellow-300 border border-yellow-500/40"}>
                          {t.approved ? "Published" : "Pending"}
                        </Badge>
                      </div>
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Star key={i} size={13} className={i <= t.rating ? "fill-blaze-orange text-blaze-orange" : "text-white/20"} />
                        ))}
                      </div>
                    </div>
                    {t.role && <p className="mt-1 text-xs text-white/50">{t.role}</p>}
                    <p className="mt-3 text-sm text-white/80 leading-relaxed italic">"{t.message}"</p>
                    <div className="mt-4 flex items-center gap-2">
                      {t.approved ? (
                        <Button
                          size="sm"
                          data-testid={`unpublish-${t.id}`}
                          onClick={() => setApproval(t.id, false)}
                          className="bg-white/5 hover:bg-white/10 border border-white/15 text-white/80 rounded-full"
                        >
                          <X size={15} className="mr-1" /> Unpublish
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          data-testid={`approve-${t.id}`}
                          onClick={() => setApproval(t.id, true)}
                          className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-full"
                        >
                          <Check size={15} className="mr-1" /> Approve
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`delete-testimonial-${t.id}`}
                        onClick={() => deleteTestimonial(t.id)}
                        className="text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded-full ml-auto"
                      >
                        <Trash2 size={15} />
                      </Button>
                    </div>
                    <p className="mt-3 text-xs text-white/35">{fmtDate(t.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-2xl border p-5 ${accent ? "border-blaze-orange/40 bg-blaze-orange/10" : "border-white/10 bg-ink-700"}`}>
      <div className="flex items-center justify-between">
        <Icon size={20} className={accent ? "text-blaze-orange" : "text-blaze-blueLight"} />
      </div>
      <p className="mt-3 font-display font-black text-4xl leading-none">{value}</p>
      <p className="mt-1 text-xs uppercase tracking-wider text-white/50">{label}</p>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="rounded-2xl border border-dashed border-white/15 bg-ink-700/40 p-12 text-center text-white/50">
      <Inbox className="mx-auto mb-3 text-white/30" size={36} />
      {label}
    </div>
  );
}
